import { Modal, SubmitButton, Switch } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import pluralize from "pluralize";
import React, { useState } from "react";
import { OversellPolicy } from "types";

interface SetVariantContinueSellingModalProps {
    isOpen: boolean;
    selectedCount: number;
    onClose: () => void;
    onApply: (oversellPolicy: OversellPolicy) => Promise<void> | void;
}

interface ModalContentProps {
    selectedCount: number;
    isSubmitting: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
    onClose: () => void;
    onRequestClose: () => void;
    onApply: (oversellPolicy: OversellPolicy) => Promise<void> | void;
}

const ModalContent = ({
    selectedCount,
    isSubmitting,
    setIsSubmitting,
    onClose,
    onRequestClose,
    onApply,
}: ModalContentProps) => {
    const [isContinueSellingEnabled, setIsContinueSellingEnabled] = useState(false);

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            await onApply(
                isContinueSellingEnabled ? OversellPolicy.ALLOW_NEGATIVE : OversellPolicy.REQUIRE_AVAILABILITY,
            );
            onClose();
        } catch {
            // updateSelectedOversellPolicy already flashed the error; keep the modal open so it can be retried
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal.Header onClose={onRequestClose}>Set Continue Selling When Out of Stock</Modal.Header>

            <Modal.Body>
                <div className="mb-4 rounded bg-blue-lighter p-3 text-sm">
                    This change will be applied to <var data-var="selectedCount">{selectedCount}</var> selected{" "}
                    <var data-var="variantLabel">{pluralize("item", selectedCount)}</var>.
                </div>

                <Switch.Group>
                    <Switch
                        data-testid="bulk-continue-selling-switch"
                        isChecked={isContinueSellingEnabled}
                        onChange={setIsContinueSellingEnabled}
                    />
                    <Switch.Label className="ml-3">Continue selling when out of stock</Switch.Label>
                </Switch.Group>
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton disabled={isSubmitting} onClick={onRequestClose}>
                    Cancel
                </SecondaryButton>
                <SubmitButton type="submit" isLoading={isSubmitting} onClick={handleSubmit}>
                    Apply
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

export const SetVariantContinueSellingModal = ({
    isOpen,
    selectedCount,
    onClose,
    onApply,
}: SetVariantContinueSellingModalProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Ignore Cancel and backdrop clicks mid-flight, otherwise a settled request
    // can close a modal the seller has already reopened.
    const handleRequestClose = () => {
        if (isSubmitting) {
            return;
        }

        onClose();
    };

    return (
        <Modal size="small" isOpen={isOpen} onClose={handleRequestClose}>
            <ModalContent
                selectedCount={selectedCount}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
                onClose={onClose}
                onRequestClose={handleRequestClose}
                onApply={onApply}
            />
        </Modal>
    );
};
